//
/* GS://PROTOCOLO
 * Arquivo: src/pages/SuppliersPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 2.15.0 – FIX: Erro Crítico de Salvamento (Campo Type)
 * Impacto da Versão: [src/pages/SuppliersPage.js]
 * Data: 21/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - CORREÇÃO CRÍTICA: O campo 'type' agora é normalizado para 'null' em vez de 'undefined'.
 * Isso resolve o erro "Unsupported field value: undefined" que impedia o salvamento.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSuppliers, addSupplier, updateSupplier, deleteSupplier } from '../services/firestoreService';
// eslint-disable-next-line no-unused-vars
import { collection, query, where, getDocs } from 'firebase/firestore'; 
// eslint-disable-next-line no-unused-vars
import { db } from '../firebase/config';
import SupplierForm from '../components/forms/SupplierForm';
import { PlusCircle, Loader2, Trash2, Pencil, Package, Droplets, Layers, Printer } from 'lucide-react';

const SuppliersPage = ({ openModal, closeModal }) => {
    const [suppliers, setSuppliers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [formKey, setFormKey] = useState(0); 
    const submissionLock = useRef(false);

    const getSupplierTypeConfig = (type) => {
        const normalized = String(type || '').toUpperCase().trim();

        if (normalized === 'BRUTO') return { label: 'Matéria Prima', color: 'bg-stone-100 text-stone-800 border-stone-200', icon: <Package size={14} className="mr-1"/> };
        if (normalized === 'BANHO') return { label: 'Banho/Galvânica', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: <Droplets size={14} className="mr-1"/> };
        if (normalized === 'AMBOS') return { label: 'Misto', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: <Layers size={14} className="mr-1"/> };
        if (normalized === 'EMBALAGEM') return { label: 'Embalagem', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: null };
        if (normalized === 'SERVICO') return { label: 'Serviço', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: null };

        return { label: 'Não definido', color: 'bg-gray-50 text-gray-400 border-gray-100', icon: null };
    };

    // [GS-FIX-CRITICAL]: Normalização corrigida
    const normalizeSupplier = (data) => {
        if (!data) return {};
        return {
            ...data,
            name: String(data.name || '').trim(),
            email: String(data.email || '').trim().toLowerCase(),
            phone: String(data.phone || '').replace(/\D/g, ''),
            address: data.address,
            createdAt: data.createdAt || new Date().toISOString(),
            // AQUI ESTAVA O ERRO: || undefined quebrava o Firebase. Alterado para || null.
            type: String(data.type || '').toUpperCase().trim() || null
        };
    };

    const fetchSuppliers = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await getSuppliers();
            setSuppliers(data);
        } catch (error) {
            console.error("Erro ao carregar fornecedores:", error);
            openModal?.(
                <p className="text-red-500">Falha ao carregar dados dos fornecedores. Verifique sua conexão.</p>,
                "Erro de Conexão"
            );
        } finally {
            setIsLoading(false);
        }
    }, [openModal]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    const handleSave = useCallback(async (supplierData, id) => {
        if (submissionLock.current) return;
        submissionLock.current = true;

        try {
            const normalizedData = normalizeSupplier(supplierData);

            if (!normalizedData.name || !normalizedData.phone || 
                !normalizedData.address?.street || !normalizedData.address?.number || 
                !normalizedData.address?.neighborhood || !normalizedData.address?.city || 
                !normalizedData.address?.state) {
                
                throw new Error('Por favor, preencha todos os campos obrigatórios, incluindo o endereço completo.');
            }

            if (id) {
                await updateSupplier(id, normalizedData);
                setSuppliers(prev => prev.map(s => 
                    s.id === id ? { ...s, ...normalizedData } : s
                ));
                openModal?.(<p className="text-green-600 font-medium">Fornecedor atualizado com sucesso!</p>, "Sucesso");
            } else {
                const result = await addSupplier(normalizedData);
                if (!result?.id) throw new Error('Falha no cadastro do fornecedor. ID não retornado.');
                
                setSuppliers(prev => [
                    { ...normalizedData, id: result.id, friendlyCode: result.friendlyCode },
                    ...prev
                ]);
                openModal?.(<p className="text-green-600 font-medium">Fornecedor cadastrado com sucesso! Código: {result.friendlyCode}</p>, "Sucesso");
            }

            closeModal?.();
        } catch (error) {
            console.error("Erro no processamento:", error.message, error);
            openModal?.(
                <div className="p-4">
                    <p className="text-red-600 font-medium">Erro ao Salvar</p>
                    <p className="text-gray-600 mt-2">
                        {error.message || 'Ocorreu um erro inesperado ao salvar o fornecedor.'}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">Detalhe: {error.code || 'Verifique o console'}</p>
                </div>,
                "Status do Cadastro"
            );
        } finally {
            submissionLock.current = false;
        }
    }, [openModal, closeModal]); 

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir este fornecedor permanentemente? Esta ação é irreversível.")) return;
        
        try {
            await deleteSupplier(id);
            setSuppliers(prev => prev.filter(s => s.id !== id));
            openModal?.(<p className="text-green-600">Fornecedor excluído com sucesso!</p>, "Sucesso");
        } catch (error) {
            console.error("Erro ao excluir fornecedor:", error);
            openModal?.(
                <p className="text-red-600">Não foi possível excluir o fornecedor. {error.message}</p>,
                "Erro na Exclusão"
            );
        }
    }, [openModal]);

    const handleEdit = useCallback((supplier) => {
        setFormKey(prev => prev + 1);
        openModal?.(
            <SupplierForm 
                key={`edit-${supplier.id}-${formKey}`}
                onClose={closeModal}
                onSave={handleSave}
                supplierData={supplier}
            />,
            "Editar Fornecedor",
            '2xl'
        );
    }, [openModal, closeModal, handleSave, formKey]);

    const handleNew = useCallback(() => {
        setFormKey(prev => prev + 1);
        openModal?.(
            <SupplierForm 
                key={`new-${formKey}`}
                onClose={closeModal}
                onSave={handleSave}
            />,
            "Cadastrar Novo Fornecedor", 
            '2xl'
        );
    }, [openModal, closeModal, handleSave, formKey]);

    const onPrint = () => {
        window.print();
    };

    return (
        <>
            <style>
                {`
                    @media print {
                        body { visibility: hidden; background: white; }
                        #root, main, .app-container { margin: 0 !important; padding: 0 !important; width: 100% !important; }
                        #printable-report {
                            visibility: visible;
                            position: fixed;
                            left: 0 !important;
                            top: 0 !important;
                            width: 100vw !important;
                            height: 100vh !important;
                            background: white;
                            z-index: 2147483647;
                            padding: 20px;
                        }
                        #printable-report * { visibility: visible; }
                        nav, aside, header, footer { display: none !important; }
                    }
                `}
            </style>

            <div className="p-8 bg-gray-50 min-h-screen font-sans print:hidden">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Fornecedores</h1>
                        <p className="text-sm text-gray-500 mt-1">Gerenciamento de parceiros e insumos</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={handleNew}
                            disabled={isLoading}
                            className="flex items-center bg-brand-gold text-brand-black font-semibold px-6 py-2.5 rounded-lg shadow-md hover:bg-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <PlusCircle className="mr-2" size={20} />
                            Novo Fornecedor
                        </button>
                        <button 
                            onClick={onPrint}
                            className="flex items-center bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2.5 rounded-lg shadow-sm hover:bg-gray-50 transition-all"
                        >
                            <Printer className="mr-2" size={20} />
                            Imprimir Lista
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm text-left text-gray-600">
                        <thead className="text-xs text-gray-600 uppercase bg-gray-100 border-b border-gray-200 font-semibold tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Código</th>
                                <th className="px-6 py-4">Nome</th>
                                <th className="px-6 py-4">Tipo</th>
                                <th className="px-6 py-4">Contato</th>
                                <th className="px-6 py-4">Endereço</th>
                                <th className="px-6 py-4 text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="6" className="text-center p-10">
                                        <Loader2 className="animate-spin mx-auto text-brand-gold" size={32} />
                                        <p className="mt-2 text-gray-400">Carregando dados...</p>
                                    </td>
                                </tr>
                            ) : suppliers.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center p-10 text-gray-400">
                                        Nenhum fornecedor cadastrado
                                    </td>
                                </tr>
                            ) : (
                                suppliers.map((supplier) => {
                                    const typeConfig = getSupplierTypeConfig(supplier.type);
                                    return (
                                        <tr key={supplier.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-mono font-bold text-gray-700">
                                                {supplier.friendlyCode || '-'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {supplier.name}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${typeConfig.color}`}>
                                                    {typeConfig.icon}
                                                    {typeConfig.label}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-gray-700">{supplier.phone}</span>
                                                    <span className="text-xs text-gray-400">{supplier.email || '(E-mail não cadastrado)'}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {typeof supplier.address === 'object' && supplier.address !== null ? (
                                                    <>
                                                        {supplier.address.street}, {supplier.address.number}<br/>
                                                        {supplier.address.neighborhood}, {supplier.address.city} - {supplier.address.state}
                                                    </>
                                                ) : (
                                                    supplier.address || '-'
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleEdit(supplier)}
                                                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                                                        title="Editar"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(supplier.id)}
                                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                                                        title="Excluir"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div id="printable-report" className="hidden print:block bg-white text-black">
                <div className="mb-6 border-b-2 border-black pb-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold uppercase tracking-wide">Golden Sisters</h1>
                            <h2 className="text-lg text-gray-600 mt-1">Lista de Fornecedores</h2>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                            <p>Total Registros: {suppliers.length}</p>
                            <p>Gerado em: {new Date().toLocaleString('pt-BR')}</p>
                        </div>
                    </div>
                </div>

                <table className="w-full text-xs text-left border-collapse">
                    <thead>
                        <tr className="border-b-2 border-black">
                            <th className="py-2 pr-2 font-bold uppercase w-16">Cód</th>
                            <th className="py-2 pr-2 font-bold uppercase">Nome / Tipo</th>
                            <th className="py-2 pr-2 font-bold uppercase">Contato</th>
                            <th className="py-2 font-bold uppercase">Endereço</th>
                        </tr>
                    </thead>
                    <tbody>
                        {suppliers.map((supplier, idx) => {
                            const typeConfig = getSupplierTypeConfig(supplier.type);
                            return (
                                <tr key={supplier.id} className={`border-b border-gray-300 ${idx % 2 === 0 ? 'bg-gray-50 print:bg-transparent' : ''}`}>
                                    <td className="py-2 pr-2 align-top font-mono font-bold">
                                        {supplier.friendlyCode || '-'}
                                    </td>
                                    <td className="py-2 pr-2 align-top">
                                        <span className="block font-bold text-sm">{supplier.name}</span>
                                        <span className="text-[10px] uppercase text-gray-500">{typeConfig.label}</span>
                                    </td>
                                    <td className="py-2 pr-2 align-top">
                                        <span className="block">{supplier.phone}</span>
                                        <span className="block text-gray-500 text-[10px]">{supplier.email}</span>
                                    </td>
                                    <td className="py-2 align-top">
                                        {typeof supplier.address === 'object' && supplier.address !== null ? (
                                            <>
                                                {supplier.address.street}, {supplier.address.number} - {supplier.address.neighborhood}<br/>
                                                {supplier.address.city}/{supplier.address.state}
                                            </>
                                        ) : (
                                            supplier.address || '-'
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="mt-8 text-[10px] text-center text-gray-400 border-t border-gray-200 pt-2">
                    <p>Ecossistema GoldenSisters - Relatório Interno</p>
                </div>
            </div>
        </>
    );
};

export default SuppliersPage;